/**
 * <VolumetricBudgetMeter> — backend-powered shipping weight / slab UX.
 *
 * 👋 USE THIS in every listing form + fulfilment screen where a vendor
 * enters L, W, H, weight. It calls /api/volumetric/preview which runs
 * the canonical `services.shipping_volumetric` calculator.
 *
 * Design goals (per iter-210):
 *  1. Zero-regression: collapsed single-line strip by default. Only
 *     expands on warning / error, so existing listing forms stay tight.
 *  2. No hardcoded rates. The meter only talks in slabs + volume; real
 *     ₹ cost (if needed) comes from a separate <ShippingCostPreview>.
 *  3. Visualises the "volume budget" progress bar + a reference object
 *     (shoe-box / laptop-box) so vendors can sanity-check without a
 *     measuring tape.
 *
 * Props:
 *   length, width, height (cm), weight (kg)  — required to render
 *   debounceMs            — debounce on input changes (default 400)
 *   mode                  — 'listing' | 'fulfilment' (cosmetic only)
 *   onValidationChange    — (result) => void; fired after each /validate
 *                           call so parent can gate the Save button
 *   compact               — if true, always stays in strip mode (no expand)
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, Box, Ruler,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VolumetricBudgetMeter({
  length,
  width,
  height,
  weight,
  debounceMs = 400,
  mode = 'listing',
  onValidationChange,
  compact = false,
  defaultExpanded = false,
  dataTestIdPrefix = 'volumetric-meter',
}) {
  const [data, setData] = useState(null);        // /preview response
  const [validation, setValidation] = useState(null); // /validate response
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  const hasAllInputs =
    Number(length) > 0 && Number(width) > 0 &&
    Number(height) > 0 && Number(weight) > 0;

  useEffect(() => {
    if (!hasAllInputs) {
      setData(null);
      setValidation(null);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        const body = {
          length_cm: Number(length),
          width_cm: Number(width),
          height_cm: Number(height),
          weight_kg: Number(weight),
        };
        const [previewResp, validResp] = await Promise.all([
          fetch(`${BACKEND_URL}/api/volumetric/preview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
          fetch(`${BACKEND_URL}/api/volumetric/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }),
        ]);
        if (myReq !== reqIdRef.current) return;
        if (previewResp.ok) {
          const j = await previewResp.json();
          setData(j);
        }
        if (validResp.ok) {
          const v = await validResp.json();
          setValidation(v);
          if (onValidationChange) onValidationChange(v);
        }
      } catch (e) {
        // silent — volumetric is non-blocking UI
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [length, width, height, weight, hasAllInputs, debounceMs, onValidationChange]);

  // Auto-expand on error or volumetric-billed warning (slab jump must be visible)
  useEffect(() => {
    if (compact) return;
    if (validation && !validation.ok) {
      setExpanded(true);
    } else if (data?.weight?.is_volumetric_billed) {
      setExpanded(true);
    }
  }, [validation, data, compact]);

  if (!hasAllInputs) return null;
  if (!data) {
    return (
      <div
        className="mt-2 px-3 py-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md flex items-center gap-2"
        data-testid={`${dataTestIdPrefix}-loading`}
      >
        <Ruler className="h-3.5 w-3.5 animate-pulse" />
        Calculating shipping weight…
      </div>
    );
  }

  const { weight: w, budget, volume_utilization_pct, within_budget } = data;
  const isError = validation && !validation.ok;
  const isWarning = !isError && !within_budget;
  const isOk = !isError && within_budget;

  // Color palette
  const tone = isError ? 'red' : isWarning ? 'amber' : 'emerald';
  const toneConfig = {
    emerald: {
      bar:    'bg-emerald-500',
      bg:     'bg-emerald-50',
      border: 'border-emerald-200',
      text:   'text-emerald-800',
      textMuted: 'text-emerald-700',
      icon:   <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
    },
    amber: {
      bar:    'bg-amber-500',
      bg:     'bg-amber-50',
      border: 'border-amber-300',
      text:   'text-amber-900',
      textMuted: 'text-amber-700',
      icon:   <AlertTriangle className="h-4 w-4 text-amber-600" />,
    },
    red: {
      bar:    'bg-red-500',
      bg:     'bg-red-50',
      border: 'border-red-300',
      text:   'text-red-900',
      textMuted: 'text-red-700',
      icon:   <XCircle className="h-4 w-4 text-red-600" />,
    },
  }[tone];

  // Strip summary
  const summary = isError
    ? `Dimensions need attention — will bill as ${w.billing_slab_kg} kg`
    : isWarning
      ? `Bulkier than weight — will bill as ${w.billing_slab_kg} kg (not ${w.physical_slab_kg} kg)`
      : `Within ${w.billing_slab_kg} kg shipping budget`;

  const canExpand = !compact;
  const pctForBar = Math.min(100, Math.max(0, volume_utilization_pct));

  return (
    <div
      className={`mt-2 border rounded-md ${toneConfig.bg} ${toneConfig.border} text-sm transition-all`}
      data-testid={dataTestIdPrefix}
    >
      {/* Strip header */}
      <button
        type="button"
        onClick={() => canExpand && setExpanded(e => !e)}
        className={`w-full px-3 py-2 flex items-center justify-between gap-2 ${canExpand ? 'cursor-pointer hover:bg-black/5' : ''}`}
        disabled={!canExpand}
        data-testid={`${dataTestIdPrefix}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {toneConfig.icon}
          <span className={`truncate font-medium ${toneConfig.text}`}>{summary}</span>
          {loading && (
            <span className="text-xs text-gray-400">(updating…)</span>
          )}
        </div>
        {canExpand && (
          expanded
            ? <ChevronUp className={`h-4 w-4 ${toneConfig.textMuted}`} />
            : <ChevronDown className={`h-4 w-4 ${toneConfig.textMuted}`} />
        )}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-black/5 space-y-3">
          {/* Volume budget progress bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className={toneConfig.textMuted}>
                Volume budget for {budget.current_slab_kg} kg slab:
                <span className="font-semibold ml-1">
                  {budget.max_volume_cm3.toLocaleString()} cm³
                </span>
                {budget.reference_object?.name && budget.reference_object.name !== '—' && (
                  <span className="text-gray-500 ml-1">
                    (≈ {budget.reference_object.name})
                  </span>
                )}
              </span>
              <span className={`font-semibold ${toneConfig.text}`} data-testid={`${dataTestIdPrefix}-utilization`}>
                {volume_utilization_pct}%
              </span>
            </div>
            <div className="h-2 w-full bg-white/70 rounded-full overflow-hidden border border-black/5">
              <div
                className={`h-full ${toneConfig.bar} transition-all duration-300`}
                style={{ width: `${pctForBar}%` }}
                data-testid={`${dataTestIdPrefix}-bar`}
              />
            </div>
            <div className="text-[11px] text-gray-500 mt-1">
              Your package: <span className="font-medium">{w.volume_cm3.toLocaleString()} cm³</span>
              {' · '}
              {w.length_cm}×{w.width_cm}×{w.height_cm} cm @ {w.physical_kg} kg
            </div>
          </div>

          {/* Weight breakdown */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Metric label="Physical" value={`${w.physical_kg} kg`} />
            <Metric label="Volumetric" value={`${w.volumetric_kg} kg`}
              highlight={w.is_volumetric_billed} />
            <Metric label="Billed slab" value={`${w.billing_slab_kg} kg`}
              highlight={w.is_volumetric_billed} />
          </div>

          {/* Advice */}
          {isWarning && (
            <div className="text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded p-2">
              <Box className="inline h-3.5 w-3.5 mr-1" />
              To bill at {w.physical_slab_kg} kg, your box must fit within{' '}
              <strong>{budget.max_volume_cm3.toLocaleString()} cm³</strong>
              {budget.reference_object?.name !== '—' && (
                <> (roughly a {budget.reference_object.name.toLowerCase()})</>
              )}.
              A smaller box means lower shipping cost for buyers.
            </div>
          )}

          {isError && validation?.errors?.length > 0 && (
            <div className="text-xs text-red-800 bg-red-100/60 border border-red-200 rounded p-2 space-y-1">
              {validation.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-1">
                  <XCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{err}</span>
                </div>
              ))}
            </div>
          )}

          {isOk && mode === 'listing' && (
            <div className="text-xs text-emerald-800">
              Your product will ship at the declared <strong>{w.physical_slab_kg} kg</strong> slab.
              Shipping cost is shown accurately to buyers at checkout.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className={`rounded bg-white/70 px-2 py-1.5 border ${highlight ? 'border-amber-300' : 'border-black/5'}`}>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-sm font-semibold ${highlight ? 'text-amber-900' : 'text-gray-800'}`}>{value}</div>
    </div>
  );
}
